<!-- Institute of Design and Innovation (email idi@unm.edu). -->

<!-- Appointment type editing page -->
<template>
  <av-modal class="appointment-type-form" :visible="visible" @close="close">
    <div class="modal-header">{{ editing ? 'Edit' : 'New'}} Appointment Type</div>
    <div class="modal-content">
      <div class="av-form-field">
        <label class="av-label">Name</label>
        <input class="av-input" type="text" v-model="editForm.name" />
      </div>

      <div class="av-form-field">
        <label class="av-label">Description</label>
        <textarea class="av-input" v-model="editForm.description"></textarea>
      </div>

      <div class="av-form-field" v-if="!editing">
        <av-radio-group v-model="editForm.isTask">
          <av-radio-button :value="false">Appointment</av-radio-button>
          <av-radio-button :value="true">Task</av-radio-button>
        </av-radio-group>
      </div>

      <div class="av-form-field">
        <av-radio-group v-model="editForm.public">
          <av-radio-button :value="true">Public</av-radio-button>
          <av-radio-button :value="false">Private</av-radio-button>
        </av-radio-group>
      </div>

      <div class="av-form-field">
        <label class="av-label">Duration</label>
        <input class="av-input" v-model.number="editForm.duration" placeholder="Duration in minutes" />
      </div>

      <div class="av-form-field">
        <label class="av-label">Color</label>
        <div class="color-picker-wrap">
        <div id="color-picker"></div>
        <div class="color-preview" :style="{ color: editForm.color, background: editForm.color + '33'}">
          Preview
        </div>
        </div>
      </div>
    </div>
    <div class="modal-actions">
      <av-button class="hollow" color="error" @click="close">Cancel</av-button>
      <av-button v-if="editing" @click="editAppointmentType" :disabled="!isValid">Save</av-button>
      <av-button v-if="!editing" @click.once="createAppointmentType" :disabled="!isValid">Create</av-button>
    </div>
  </av-modal>
</template>

<script lang="ts">
import iro from '@jaames/iro'
import { defineComponent, PropType } from 'vue'
import AppointmentTypeService from '../common/appointmentTypeService'
import { AppointmentType } from '../models/appointmentType'

export default defineComponent({
  name: 'AppointmentTypeForm',

  props: {
    /** The appointment type to edit */
    appointmentType: {
      type: Object as PropType<AppointmentType>,
      required: false
    },

    /** is appointment type being edited */
    editing: {
      type: Boolean,
      default: false,
      required: false
    },

    /** Indicates if the modal is visible or not */
    visible: {
      type: Boolean,
      required: true,
      default: false
    }
  },

  data () {
    return {
      /** The working copy of the appointment type to edit/create */
      editForm: {} as AppointmentType
    }
  },

  computed: {
    /** Indicaves if a type is being edited or created */
    isEditing (): boolean {
      return this.appointmentType !== null
    },

    /** indicates if appointment type is valid */
    isValid (): boolean {
      if (!this.visible) {
        return false
      }

      if (this.editForm.name === '' || this.editForm.description === '' || this.editForm.duration === 0 || (this.editForm.duration as any) === '') {
        return false
      }

      return true
    }
  },

  watch: {
    visible: {
      handler (val) {
        if (!val) {
          return
        }

        this.resetForm()

        setTimeout(() => {
          const colorPicker = iro.ColorPicker('#color-picker', {
            width: 120,
            color: this.appointmentType ? this.appointmentType.color : '#CCC'
          })

          colorPicker.on(['color:init', 'color:change'], this.setColor)
        }, 0)
      }
    }
  },

  emits: ['close', 'updated'],

  methods: {
    /**
     * Emits an event to close the modal
     */
    close () {
      this.$emit('close')
    },

    /**
     * Creates a new AppoiontmentType
     */
    createAppointmentType () {
      AppointmentTypeService.create(this.editForm).then(res => {
        this.$emit('updated', res.data)
      })
    },

    /**
     * Updates the appointment types's general info
     */
    editAppointmentType () {
      AppointmentTypeService.update(this.editForm).then(res => {
        this.$emit('updated', res.data)
      })
    },

    /**
     * Resets the edit form to default values
     */
    resetForm () {
      this.editForm = {
        id: this.appointmentType ? this.appointmentType.id : 0,
        name: this.appointmentType ? this.appointmentType.name : '',
        description: this.appointmentType ? this.appointmentType.description : '',
        duration: this.appointmentType ? this.appointmentType.duration : 60,
        public: this.appointmentType ? this.appointmentType.public : false,
        isTask: this.appointmentType ? this.appointmentType.isTask : false,
        removed: this.appointmentType ? this.appointmentType.removed : false,
        color: this.appointmentType ? this.appointmentType.color : ''
      }
    },

    /**
     * Sets the edif form's color field with the given color
     * @param color: The color object from iro picker
     */
    setColor (color) {
      this.editForm.color = color.hexString
    }
  }
})
</script>
